import React from "react";
import { NavigateFunction } from "react-router-dom";
import MenuSection from "./MenuSection";
import type { MenuItem } from "../MenuData";
import { ProductInternalName } from "../productConfig";
import {
  insightMenuItems,
  managementItems,
} from "../MenuData";

export type SideMenuNavByProductProps = {
  internalName: string;
  isExpanded: boolean;
  isMobileView: boolean;
  expandedSections: { [key: string]: boolean };
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  activeItem: string;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  navigate: NavigateFunction;
  computedMainItems: MenuItem[];
  filteredServiceItems: MenuItem[];
  eventManagementItemsForMenu: MenuItem[];
  reflectorV2MenuSections: MenuItem[];
  /** Closing "Account" section, already filtered for this tenant (CTX-512). */
  accountItems: MenuItem[];
};

/**
 * Main product nav: Overview / Services / Monitoring / Management, closed by
 * the shared Account section (CTX-512).
 *
 * Account is appended here rather than inside each product's item list because
 * it is the same section for all of them — a product that forgot to include it
 * would silently lose the rows. What is left of the settings area (Users,
 * Customers, API, Audit Logs) still lives in the drill-in reached from the
 * account card.
 */
const SideMenuNavByProduct: React.FC<SideMenuNavByProductProps> = ({
  internalName,
  isExpanded,
  isMobileView,
  expandedSections,
  setExpandedSections,
  activeItem,
  setActiveItem,
  navigate,
  computedMainItems,
  filteredServiceItems,
  eventManagementItemsForMenu,
  reflectorV2MenuSections,
  accountItems,
}) => {
  const showLabels = isExpanded || isMobileView;
  const onPathNavigate = (item: MenuItem) => {
    if (item.path) navigate(item.path);
  };

  /* Every product closes with the same section, so it is built once here and
     rendered by each branch below. Empty only if a tenant is gated out of all
     of its rows, in which case the section header would stand alone. */
  const accountSection = accountItems.length > 0 && (
    <MenuSection
      title={showLabels ? "Account" : ""}
      items={accountItems}
      expandedSections={expandedSections}
      setExpandedSections={setExpandedSections}
      activeItem={activeItem}
      setActiveItem={setActiveItem}
      isExpanded={showLabels}
      onItemClick={(item) => onPathNavigate(item)}
    />
  );

  if (internalName === ProductInternalName.CtxReflectorV2) {
    return (
      <>
        {reflectorV2MenuSections.map((filteredSection) => (
          <MenuSection
            key={filteredSection.id || filteredSection.label}
            title={showLabels ? filteredSection.label : ""}
            items={
              filteredSection.children ? filteredSection.children : [filteredSection]
            }
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            isExpanded={showLabels}
            onItemClick={(item) => onPathNavigate(item)}
          />
        ))}
        {accountSection}
      </>
    );
  }

  if (internalName === ProductInternalName.Insight) {
    return (
      <>
        {insightMenuItems
          .filter((section) => {
            return (
              section.label?.toLowerCase() !== "overview" &&
              section.id?.toLowerCase() !== "overview"
            );
          })
          .map((section) => (
            <MenuSection
              key={section.id || section.label}
              title={showLabels ? section.label : ""}
              items={section.children ? section.children : [section]}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              isExpanded={showLabels}
              onItemClick={(item) => onPathNavigate(item)}
            />
          ))}
        {accountSection}
      </>
    );
  }

  // Kontrol Edge (and CTX Reflector v1).
  return (
    <>
      {/* Overview renders as a standalone item (no "Main" header) per the redesign. */}
      <MenuSection
        title=""
        items={computedMainItems}
        expandedSections={expandedSections}
        setExpandedSections={setExpandedSections}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onItemClick={(item) => onPathNavigate(item)}
        isExpanded={showLabels}
      />
      <MenuSection
        title={showLabels ? "Services" : ""}
        items={filteredServiceItems}
        expandedSections={expandedSections}
        setExpandedSections={setExpandedSections}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isExpanded={showLabels}
        onItemClick={(item) => onPathNavigate(item)}
      />
      {eventManagementItemsForMenu.length > 0 && (
        <MenuSection
          title={showLabels ? "Monitoring" : ""}
          items={eventManagementItemsForMenu}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          isExpanded={showLabels}
          onItemClick={(item) => onPathNavigate(item)}
        />
      )}
      {internalName === ProductInternalName.CtxReflector && (
        <MenuSection
          title={showLabels ? "Management" : ""}
          items={managementItems}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          isExpanded={showLabels}
          onItemClick={(item) => onPathNavigate(item)}
        />
      )}
      {accountSection}
    </>
  );
};

export default SideMenuNavByProduct;
